import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Logo } from "@/components/logo";
import { lovable } from "@/integrations/lovable";
import i18n from "@/lib/i18n";

import { seoMeta } from "@/lib/seo/head";

export const Route = createFileRoute("/login")({
  head: () => seoMeta({
    title: i18n.t("auth.seoLoginTitle"),
    description: i18n.t("auth.seoLoginDesc"),
    path: "/login",
    noindex: true,
  }),
  component: LoginPage,
});

function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [oauthProcessing, setOauthProcessing] = useState(false);

  useEffect(() => {
    const hash = typeof window !== "undefined" ? window.location.hash : "";
    const hasOAuthHash = hash.includes("access_token=") || hash.includes("error=");
    if (hasOAuthHash) setOauthProcessing(true);

    const finish = async () => {
      const { data } = await supabase.auth.getSession();
      if (!data.session) return;
      try { window.history.replaceState(null, "", window.location.pathname + window.location.search); } catch {}
      const { data: profile } = await supabase
        .from("profiles").select("onboarded").eq("id", data.session.user.id).maybeSingle();
      if (profile && profile.onboarded === false) navigate({ to: "/onboarding" });
      else navigate({ to: "/app" });
    };

    finish();
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if ((event === "SIGNED_IN" || event === "INITIAL_SESSION") && session) finish();
    });
    return () => sub.subscription.unsubscribe();
  }, [navigate]);

  const handleApple = async () => {
    setAppleLoading(true);
    const result = await lovable.auth.signInWithOAuth("apple", { redirect_uri: `${window.location.origin}/app` });
    if (result.error) { toast.error(t("auth.appleError")); setAppleLoading(false); return; }
    if (!result.redirected) navigate({ to: "/app" });
  };

  const handleGoogle = async () => {
    setGoogleLoading(true);
    const result = await lovable.auth.signInWithOAuth("google", { redirect_uri: `${window.location.origin}/app` });
    if (result.error) { toast.error(t("auth.googleError")); setGoogleLoading(false); return; }
    if (!result.redirected) navigate({ to: "/app" });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(t("auth.signedIn"));
    navigate({ to: "/app" });
  };


  if (oauthProcessing) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-background">
        <div className="text-center space-y-3">
          <Logo asLink={false} variant="light" size={48} />
          <p className="text-muted-foreground text-sm">{t("auth.signinInProgress")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-background">
      <div className="w-full max-w-md space-y-8">
        <div className="flex justify-center">
          <Logo variant="light" size={48} />
        </div>
        <div className="space-y-2 text-center">
          <h1 className="text-3xl font-serif">{t("auth.loginTitle")}</h1>
          <p className="text-muted-foreground">{t("auth.loginSubtitle")}</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">{t("auth.email")}</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">{t("auth.password")}</Label>
            <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? t("auth.signingIn") : t("auth.signIn")}
          </Button>
          <div className="text-right">
            <Link to="/forgot-password" className="text-sm text-muted-foreground hover:text-primary hover:underline">
              Password dimenticata?
            </Link>
          </div>
        </form>
        <div className="relative my-2">
          <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
          <div className="relative flex justify-center text-xs uppercase"><span className="bg-background px-2 text-muted-foreground">{t("auth.or")}</span></div>
        </div>
        <Button type="button" variant="outline" className="w-full" disabled={googleLoading} onClick={handleGoogle}>
          <svg className="mr-2 h-4 w-4" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35.5 24 35.5c-6.4 0-11.5-5.1-11.5-11.5S17.6 12.5 24 12.5c2.9 0 5.6 1.1 7.6 2.9l5.7-5.7C33.6 6.3 29.1 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5c10.7 0 19.5-7.7 19.5-19.5 0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.8 16 19 13 24 13c2.9 0 5.6 1.1 7.6 2.9l5.7-5.7C33.6 6.8 29.1 5 24 5 16.3 5 9.7 9.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 43c5 0 9.6-1.9 13.1-5l-6.1-5c-2 1.4-4.4 2.2-7 2.2-5.3 0-9.7-3.4-11.3-8.1l-6.5 5C9.5 38.5 16.1 43 24 43z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6.1 5c-.4.4 6.7-4.9 6.7-14.5 0-1.2-.1-2.3-.4-3.5z"/></svg>
          {googleLoading ? t("auth.redirecting") : t("auth.continueGoogle")}
        </Button>
        <Button type="button" variant="outline" className="w-full" disabled={appleLoading} onClick={handleApple}>
          <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M16.365 1.43c0 1.14-.42 2.22-1.18 3.04-.81.88-2.13 1.56-3.22 1.47-.14-1.12.41-2.29 1.16-3.07.84-.88 2.27-1.54 3.24-1.44zM20.5 17.06c-.56 1.29-.83 1.87-1.55 3-.99 1.59-2.39 3.57-4.13 3.58-1.55.02-1.95-1.01-4.06-1-2.11.01-2.55 1.02-4.1 1-1.74-.01-3.06-1.79-4.05-3.38C.39 16.84-.16 11.84 1.95 9.16c1.43-1.82 3.69-2.88 5.81-2.88 2.16 0 3.52 1.18 5.31 1.18 1.74 0 2.8-1.18 5.3-1.18 1.89 0 3.9.99 5.32 2.7-4.68 2.56-3.92 9.25-1.19 8.08z"/></svg>
          {appleLoading ? t("auth.redirecting") : t("auth.continueApple")}
        </Button>
        <p className="text-center text-sm text-muted-foreground">
          {t("auth.noAccount")}{" "}
          <Link to="/signup" className="text-primary hover:underline">{t("auth.register")}</Link>
        </p>
      </div>
    </div>
  );
}
